/**
 * Saidiseire's engine settings as the settings dialog types them: the ranges
 * the route accepts and the two guards that turn typed text into a value the
 * route will not refuse.
 *
 * Mirrors `apps/api/src/modules/crm-monitoring/lib/settings.ts`, and
 * `monitoring.test.ts` holds the limits against that file's text. The limit
 * constants live here with the guards that read them, so this file imports
 * nothing and `monitoring.ts` can re-export it without a cycle.
 *
 * Pure: no React, no fetch. Imported through `monitoring.ts`.
 */

// ── Limits (shared with the route's zod) ─────────────────────────────────────

export const MONITOR_LIMITS = {
  intervalMinutes: { min: 1, max: 60, fallback: 5 },
  concurrency: { min: 1, max: 8, fallback: 4 },
} as const;

export type MonitorNumberSetting = keyof typeof MONITOR_LIMITS;

/** At most this many named alert recipients. */
export const MONITOR_RECIPIENTS_MAX = 20;
/** The dead-man's-switch address, https only. */
export const HEARTBEAT_URL_MAX = 500;

// ── Setting guards ───────────────────────────────────────────────────────────

/**
 * A typed number, clamped to what the route accepts. Text that is not a number
 * keeps the stored value rather than saving the floor: a stray letter is a typo,
 * not a request for one-minute checks.
 */
export function clampSetting(field: MonitorNumberSetting, raw: string, current: number): number {
  const text = raw.trim();
  const n = Number(text);
  if (!text || !Number.isFinite(n)) return current;
  const { min, max } = MONITOR_LIMITS[field];
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Why a typed ping address cannot be saved, or null when it can. Empty text
 *  means "leave it as it is" and is not a problem. */
export function heartbeatProblem(raw: string): "https" | "long" | null {
  const text = raw.trim();
  if (!text) return null;
  if (text.length > HEARTBEAT_URL_MAX) return "long";
  try {
    return new URL(text).protocol === "https:" ? null : "https";
  } catch {
    return "https";
  }
}
